// The hiring roleplay rebuild (DA-5042), in the candidate's Analysis modal: how
// a capability's level came about, marker by marker, and the hiring manager's
// way to change it.
//
//   - each marker above Weak, met, not met or uncertain, with the candidate's
//     words, Elliott's note and what an answer had to use (metWhen);
//   - the asterisk note on a provisional level;
//   - the quiz question, the candidate's answer and the correct one, and the
//     drop it caused ("Capable · Advanced in roleplay, quiz incorrect");
//   - Override: a level and a reason, which is logged in the round's Activity.

import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { hiringApi, hiringMessage, type HiringCandidateCapabilityView, type HiringRoundView } from '../../../../api/hiring';
import { ActionButton, BODY, DANGER, INPUT_SX, LABEL, TEXT, TEXT_2, TEXT_3, TEXTAREA_SX } from '../capability-setup/designSystem';
import { LEVEL_COLOR, LEVEL_NAME } from '../Program/components/learning-lifecycle/CapabilityGapsList';
import { LEVEL_ORDER, type CapabilityLevel } from '../Program/components/learning-lifecycle/teamCapability';
import { GRID_LINE } from './hiringParts';

const RESULT: Record<string, { word: string; color: string }> = {
  met: { word: 'Met', color: '#22C55E' },
  notMet: { word: 'Not met', color: TEXT_3 },
  uncertain: { word: 'Uncertain', color: '#E8A33D' }
};

const MIN_REASON = 10;

const CapabilityMarkers: React.FC<{
  capability: HiringCandidateCapabilityView;
  round: HiringRoundView;
  candidateId: string;
  org?: string;
  /** The round is closed or the candidate's result can't change: no Override. */
  readOnly?: boolean;
  onChanged: () => void;
}> = ({ capability, round, candidateId, org, readOnly, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [level, setLevel] = useState<CapabilityLevel>((capability.level as CapabilityLevel) || 'capable');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const item = round.items.find((i) => i.capabilityId === capability.capabilityId);
  const markers = capability.markers || [];
  if (!capability.roleplayLevel) return null;

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await hiringApi.overrideLevel(candidateId, capability.capabilityId, { level, reason: reason.trim() }, org);
      setOpen(false);
      setReason('');
      onChanged();
    } catch (err) {
      setError(hiringMessage(err, "The level couldn't be changed."));
    } finally {
      setBusy(false);
    }
  };

  const answer = capability.quiz.answerIndex;
  return (
    <Box data-testid={`markers-${capability.capabilityId}`} sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <Box>
        <Typography sx={{ ...LABEL, color: TEXT, mb: '6px' }}>Markers</Typography>
        <Box sx={{ border: GRID_LINE, borderRadius: '4px', '& > * + *': { borderTop: GRID_LINE } }}>
          {markers.map((m) => (
            <Box key={m.marker} data-testid={`marker-result-${m.marker}`} sx={{ p: '8px 10px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography sx={{ ...LABEL, color: LEVEL_COLOR[m.marker as CapabilityLevel], width: 90 }}>
                  {LEVEL_NAME[m.marker as CapabilityLevel]}
                </Typography>
                <Typography sx={{ ...BODY, fontSize: 12, fontWeight: 600, color: RESULT[m.result]?.color || TEXT_3 }}>
                  {RESULT[m.result]?.word || m.result}
                </Typography>
              </Box>
              {m.quote && <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT }}>“{m.quote}”</Typography>}
              {m.note && <Typography sx={{ ...BODY, fontSize: 12, color: TEXT_2 }}>{m.note}</Typography>}
              {m.metWhen && <Typography sx={{ ...BODY, fontSize: 11.5, color: TEXT_3 }}>Met when: {m.metWhen}</Typography>}
            </Box>
          ))}
        </Box>
      </Box>

      {capability.provisional && (
        <Typography role="note" sx={{ ...BODY, fontSize: 12.5, color: '#E8A33D' }}>
          * Provisional: the two gradings didn't agree, so this is the level clearly met. {capability.provisionalNote}
        </Typography>
      )}

      {item && answer !== null && (
        <Box>
          <Typography sx={{ ...LABEL, color: TEXT, mb: '4px' }}>Quiz Question</Typography>
          <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_2 }}>{item.quiz.question}</Typography>
          <Typography sx={{ ...BODY, fontSize: 12.5, color: capability.quiz.passed ? '#22C55E' : DANGER, mt: '4px' }}>
            Their answer: {item.quiz.options[answer] ?? '-'} ({capability.quiz.passed ? 'correct' : 'incorrect'})
          </Typography>
          {!capability.quiz.passed && (
            <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_2 }}>Correct answer: {item.quiz.options[item.quiz.correct] ?? '-'}</Typography>
          )}
        </Box>
      )}

      {capability.override && (
        <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_2 }}>
          Changed to {LEVEL_NAME[capability.override.level]} by {capability.override.byName || 'a hiring manager'}: {capability.override.reason}
        </Typography>
      )}

      {!readOnly &&
        (open ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Box
              component="select"
              aria-label={`${capability.capabilityName}: new level`}
              value={level}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setLevel(e.target.value as CapabilityLevel)}
              sx={{ ...INPUT_SX, width: 200 }}
            >
              {LEVEL_ORDER.map((l) => (
                <option key={l} value={l}>
                  {LEVEL_NAME[l]}
                </option>
              ))}
            </Box>
            <Box
              component="textarea"
              rows={3}
              aria-label={`${capability.capabilityName}: reason for the change`}
              placeholder="Why you are changing the level. This is logged in the round's Activity."
              value={reason}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
              sx={TEXTAREA_SX}
            />
            {error && (
              <Typography role="alert" sx={{ ...BODY, fontSize: 12.5, color: DANGER }}>
                {error}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: '8px' }}>
              <ActionButton
                primary
                onClick={save}
                disabled={busy || reason.trim().length < MIN_REASON}
                reason={reason.trim().length < MIN_REASON ? 'Give a reason of at least 10 characters' : undefined}
              >
                {busy ? 'Saving...' : 'Save level'}
              </ActionButton>
              <ActionButton onClick={() => setOpen(false)} disabled={busy}>
                Cancel
              </ActionButton>
            </Box>
          </Box>
        ) : (
          <Box>
            <ActionButton onClick={() => setOpen(true)}>Override level</ActionButton>
          </Box>
        ))}
    </Box>
  );
};

export default CapabilityMarkers;
